/**
 * Reine Hilfsfunktionen der Verortungs-Kamera (docs/verortung-kamera-vertrag.md,
 * Paket B): Texte der Einblendung und des Fotostempels, Maße des Aufnahme-Canvas,
 * Winkel des Kompass-Pfeils, Klartext der Kamerafehler.
 *
 * Kein React, kein DOM, keine Sensoren — alles hier ist ohne Browser prüfbar
 * (kameraBild.test.ts). Die Typen sind bewusst strukturell gehalten (nur `name`
 * bzw. die gebrauchten Felder), damit diese Datei nicht am Rechenkern hängt.
 */

const RICHTUNGEN = ['N', 'NO', 'O', 'SO', 'S', 'SW', 'W', 'NW'] as const;

/** Zahl deutsch mit fester Nachkommazahl, „-0“ wird „0“. */
function zahl(w: number, stellen: number): string {
    const text = w.toLocaleString('de-DE', { minimumFractionDigits: stellen, maximumFractionDigits: stellen });
    return /^-0(?:,0+)?$/.test(text) ? text.slice(1) : text;
}

const zweistellig = (n: number): string => String(n).padStart(2, '0');

/**
 * Höhe relativ ±0,00 mit Vorzeichen und einer Nachkommastelle:
 * „+6,1 m“, „−3,2 m“ (echtes Minuszeichen), gerundet null → „±0,0 m“.
 * Nicht endlich → leerer Text.
 */
export function hoeheText(hoeheM: number): string {
    if (!Number.isFinite(hoeheM)) return '';
    const gerundet = Math.round(hoeheM * 10) / 10;
    if (gerundet === 0) return '±0,0 m';
    return `${gerundet > 0 ? '+' : '−'}${zahl(Math.abs(gerundet), 1)} m`;
}

/** Datum und Uhrzeit (Ortszeit des Geräts) wie im deutschen Bericht: „27.09.2026 10:14“. */
export function datumZeitText(t: number): string {
    const d = new Date(t);
    if (!Number.isFinite(d.getTime())) return '';
    return `${zweistellig(d.getDate())}.${zweistellig(d.getMonth() + 1)}.${d.getFullYear()} `
        + `${zweistellig(d.getHours())}:${zweistellig(d.getMinutes())}`;
}

/** Was die Kamera von einer Ebenen-Schätzung braucht (strukturell zu `EbenenSchaetzung`). */
export interface EbenenAnzeigeQuelle {
    ebene: { name: string } | null;
    zwischen: readonly [{ name: string }, { name: string }] | null;
}

/**
 * Kurzname der geschätzten Ebene ohne „vermutlich“: „2. OG“ bzw.
 * „zwischen 1. OG und 2. OG“ (Treppenhaus). Ohne Schätzung → null.
 */
export function ebenenText(s: EbenenAnzeigeQuelle | null | undefined): string | null {
    if (!s) return null;
    if (s.zwischen) return `zwischen ${s.zwischen[0].name} und ${s.zwischen[1].name}`;
    const name = s.ebene?.name?.trim();
    return name ? name : null;
}

/**
 * Text des Fotostempels: „2. OG · +6,1 m · 27.09.2026 10:14“. Fehlt die Ebene
 * oder die Höhe, fällt der Teil weg; Datum und Uhrzeit stehen immer da.
 */
export function stempelText(e: { ebene: string | null; hoeheM: number | null; t: number }): string {
    const teile: string[] = [];
    const ebene = e.ebene?.trim();
    if (ebene) teile.push(ebene);
    if (e.hoeheM !== null) {
        const h = hoeheText(e.hoeheM);
        if (h) teile.push(h);
    }
    const zeit = datumZeitText(e.t);
    if (zeit) teile.push(zeit);
    return teile.join(' · ');
}

/** Ausschnitt des Videobilds und Größe des Aufnahme-Canvas in Pixeln. */
export interface CanvasMasse {
    quelle: { x: number; y: number; breite: number; hoehe: number };
    breite: number;
    hoehe: number;
}

/** Längste Kante des Aufnahme-Canvas — schützt schwache Geräte vor Riesen-Canvas. */
export const MAX_CANVAS_KANTE = 4096;

/**
 * Maße des Aufnahme-Canvas aus der Videoauflösung.
 *
 * Volle Auflösung (nur über `maxKante` wird verkleinert). Querformat wird wie
 * in `compressImage` (cropToPortrait) mittig auf 3:4 beschnitten — hier schon
 * VOR dem Fotostempel, sonst schnitte die spätere Pipeline den Stempelbalken
 * links und rechts ab. Das Ergebnis ist dasselbe Bild wie ohne Vorbeschnitt.
 * Unbrauchbare Maße (Video noch nicht bereit) → null.
 */
export function canvasZielmasse(videoBreite: number, videoHoehe: number, maxKante: number = MAX_CANVAS_KANTE): CanvasMasse | null {
    if (!Number.isFinite(videoBreite) || !Number.isFinite(videoHoehe) || videoBreite <= 0 || videoHoehe <= 0) return null;
    const grenze = Number.isFinite(maxKante) && maxKante > 0 ? maxKante : MAX_CANVAS_KANTE;
    let qBreite = videoBreite;
    let qX = 0;
    if (videoBreite > videoHoehe) {
        qBreite = Math.round(videoHoehe * (3 / 4));
        qX = Math.round((videoBreite - qBreite) / 2);
    }
    const faktor = Math.min(1, grenze / Math.max(qBreite, videoHoehe));
    return {
        quelle: { x: qX, y: 0, breite: qBreite, hoehe: videoHoehe },
        breite: Math.max(1, Math.round(qBreite * faktor)),
        hoehe: Math.max(1, Math.round(videoHoehe * faktor)),
    };
}

/** Lage des Stempelbalkens am unteren Bildrand. */
export interface StempelLayout {
    schriftPx: number;
    balkenHoehe: number;
    randPx: number;
}

/**
 * Schriftgröße und Balken des Fotostempels, bezogen auf die Bildbreite: nach
 * der Verkleinerung auf 800 px (Hochformat ≈ 450 px breit) bleibt die Schrift
 * noch ≈ 20 px hoch. Mindestens 12 px.
 */
export function stempelLayout(breite: number): StempelLayout {
    const b = Number.isFinite(breite) && breite > 0 ? breite : 0;
    const schriftPx = Math.max(12, Math.round(b * 0.045));
    return { schriftPx, balkenHoehe: Math.round(schriftPx * 1.9), randPx: Math.round(schriftPx * 0.6) };
}

/** Winkel auf [0, 360) bringen; nicht endlich → null. */
export function pfeilWinkel(grad: number | null | undefined): number | null {
    if (typeof grad !== 'number' || !Number.isFinite(grad)) return null;
    const w = ((grad % 360) + 360) % 360;
    // −0 und Rundungsreste knapp unter 360 als 0 zeigen.
    return w === 0 || w > 359.9999999 ? 0 : w;
}

/** Himmelsrichtung in acht Teilen: 42° → „NO“. */
export function himmelsrichtung(grad: number): string {
    const w = pfeilWinkel(grad) ?? 0;
    return RICHTUNGEN[Math.round(w / 45) % 8];
}

/** Anzeige der Blickrichtung: „NO 42°“; nicht endlich → leer. */
export function blickText(grad: number | null | undefined): string {
    const w = pfeilWinkel(grad);
    if (w === null) return '';
    const rund = Math.round(w) % 360;
    return `${himmelsrichtung(w)} ${rund}°`;
}

/** Alter des Nullpunkts für die kleine Zeile der Einblendung. */
export function nullpunktAlterText(minuten: number): string {
    if (!Number.isFinite(minuten) || minuten < 1) return 'Nullpunkt gerade gesetzt';
    if (minuten < 90) return `Nullpunkt vor ${Math.floor(minuten)} min`;
    return `Nullpunkt vor ${zahl(minuten / 60, 1)} h`;
}

/** Ab diesem Alter bittet die Einblendung, die Ebene neu anzutippen (Plan 9.1: 20–30 min). */
export const NULLPUNKT_ALT_MIN = 30;

/**
 * Klartext zu einem Fehler von `getUserMedia` (DOMException-Namen).
 * Recht verweigert, keine Kamera, Kamera belegt, keine Kamera-Schnittstelle, sonst allgemein.
 */
export function kameraFehlerText(e: unknown): string {
    const name = e !== null && typeof e === 'object' && 'name' in e && typeof (e as { name: unknown }).name === 'string'
        ? (e as { name: string }).name
        : '';
    const nachricht = e instanceof Error && e.message ? e.message : typeof e === 'string' ? e : '';
    switch (name) {
        case 'NotAllowedError':
        case 'PermissionDeniedError':
        case 'SecurityError':
            return 'Kamerazugriff verweigert — in den Android-Einstellungen erlauben oder Systemkamera nutzen';
        case 'NotFoundError':
        case 'DevicesNotFoundError':
        case 'OverconstrainedError':
            return 'Keine Kamera gefunden — bitte die Systemkamera nutzen';
        case 'NotReadableError':
        case 'TrackStartError':
            return 'Kamera ist belegt (andere App?) — später erneut versuchen oder Systemkamera nutzen';
        case 'NotSupportedError':
            return 'Kamera hier nicht verfügbar — bitte die Systemkamera nutzen';
        default:
            return nachricht ? `Kamera startet nicht: ${nachricht}` : 'Kamera startet nicht — bitte die Systemkamera nutzen';
    }
}
